import type { CreateOnKeyDown } from '../../types';
import type {
  PicklistPluginPropertyOptions,
  NodeSelectorState,
  PicklistPluginElement,
} from './types';
import { onKeyDownInlineTemplate } from '../common/inlineTemplate';
import { getNodeSelectedStateByNodeType } from '../../hooks';
import isHotkey from 'is-hotkey';
import { Editor, Range, Transforms } from '../../core';
import { PICKLIST_PLUGIN_ID } from './types';
import { find } from '../../utils';
import { isSelectionContainedInNode } from '../../utils/isSelectionContainedInNode';
import { logger } from 'modules/logger';
import { Element } from 'slate';
import { PARAGRAPH_PLUGIN_ID } from '../paragraph';

export const onKeyDownPicklist: CreateOnKeyDown<PicklistPluginPropertyOptions> =
  ({ pluginID, disableKeyDown, variant }) =>
  (e: KeyboardEvent, { editor, nodeState }) => {
    if (disableKeyDown === true) {
      const picklistEntry = find(editor, (n) => n.type === PICKLIST_PLUGIN_ID);

      const { selection } = editor;

      // If you are using the field pane in either the macro and template editors, we do not allow you to directly interact with
      // the picklist element in the editor. Rather, you need to edit the options in the pane. As a result, we block any keyboard interaction
      // within the picklist element that aren't entire deletions. If your cursor is entirely inside a picklist, we block all keyboard interactions.
      if (selection != null && picklistEntry != null) {
        if (isSelectionContainedInNode(editor, picklistEntry[1], editor.selection)) {
          // Selecting a picklist (via click, tab, or arrow keys) expands the
          // selection to cover the whole picklist (an expanded range). Let an
          // expanded Backspace/Delete through so the picklist can be deleted as
          // a unit - required picklists prompt the confirmation dialog
          // downstream. Every other key is still blocked so the contents can't
          // be edited directly.
          const isEntireDeletion =
            !Range.isCollapsed(selection) && (isHotkey('backspace', e) || isHotkey('delete', e));

          if (!isEntireDeletion) {
            e.preventDefault();
            logger.warn(
              `[onKeyDownPicklist] Attempt to edit picklist directly in ${variant === 'fragment' ? 'Macro' : 'Template'} Editor`
            );
          }
        }
      }
    }

    const stateAndUpdater = getNodeSelectedStateByNodeType<NodeSelectorState>({
      nodeState,
      editor,
      pluginID,
    });

    const selection = editor.selection;
    if (selection == null) {
      return;
    }

    if (stateAndUpdater) {
      const [
        { isOpen, moveItemUp, selectPicklistItem, moveItemDown, resetActiveSelectedPicklistOption },
        setState,
      ] = stateAndUpdater;

      // If selection is collapsed, that means that we are likely not trying to replace the entire
      // picklist with another option
      if (isOpen && !Range.isCollapsed(selection)) {
        const picklistEntry = Editor.above(editor, {
          at: selection,
          match: (n): n is PicklistPluginElement =>
            Element.isElement(n) && n.type === PICKLIST_PLUGIN_ID,
          mode: 'highest',
        });

        if (picklistEntry == null) {
          return onKeyDownInlineTemplate({ pluginID })(e, { editor, nodeState });
        }

        const picklistRange = Editor.range(editor, picklistEntry[1]);

        // If the selection is not equal to the edges of the picklist, that means we have
        // selected some portion of the picklist. As a result, we again are likely not trying
        // to replace the entire picklist with another option
        if (!Range.equals(picklistRange, selection)) {
          if (isHotkey('enter', e)) {
            e.preventDefault();
            Transforms.splitNodes(editor, {
              always: true,
              match: (n) => Element.isElement(n) && n.type === PARAGRAPH_PLUGIN_ID,
              mode: 'highest',
            });
            return true;
          }
        } else {
          if (isHotkey('up', e)) {
            e.preventDefault();
            moveItemUp();
            return true;
          } else if (isHotkey('down', e)) {
            e.preventDefault();
            moveItemDown();
            return true;
          } else if (isHotkey('esc', e)) {
            e.preventDefault();
            resetActiveSelectedPicklistOption();
            setState((state) => ({ ...state, isOpen: false }));
            return true;
          } else if (isHotkey('enter', e)) {
            e.preventDefault();
            selectPicklistItem();
            return true;
          }
        }
      }
    }

    return onKeyDownInlineTemplate({ pluginID })(e, { editor, nodeState });
  };
